import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Tooltip,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import { Text } from 'components';

type Currency = 'KRW' | 'USD';

type Props = {
  satelliteData: { name: string; value: number; color: string }[];
  antennaData: { name: string; value: number; color: string }[];
  paymentMethodData: { name: string; percentage: number; color: string }[];
  satelliteChartTitle: string;
  antennaChartTitle: string;
  paymentMethodChartTitle: string;
  costTooltipLabel: string;
  currency: Currency; // ▼ 추가: 표시 통화
};

const SYMBOL: Record<Currency, string> = { KRW: '₩', USD: '$' };
const formatMoney = (value: number, currency: Currency) =>
  currency === 'KRW'
    ? Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const withSymbol = (v: number, c: Currency) => `${SYMBOL[c]}${formatMoney(v, c)}`;

export const PriceMonitorSection = ({
  satelliteData,
  antennaData,
  paymentMethodData,
  satelliteChartTitle,
  antennaChartTitle,
  paymentMethodChartTitle,
  costTooltipLabel,
  currency
}: Props) => {
  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, index }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 10;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const safe = antennaData[index];
    return (
      <text
        x={x}
        y={y}
        fill={safe?.color || '#666'}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="font-['Pretendard'] text-[12px] font-medium"
      >
        {`${((percent || 0) * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* 위성별 비용 */}
      <div className="rounded-lg border border-[#f3f3f3] bg-white p-4">
        <Text size="small" weight="medium" color="primary" className="mb-4">
          {satelliteChartTitle}
        </Text>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={satelliteData}>
              <Tooltip
                cursor={{ fill: 'rgba(0,122,255,0.08)' }}
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid #f3f3f3',
                  fontFamily: 'Pretendard',
                  fontSize: 12,
                  color: '#52525b'
                }}
                formatter={(value: number) => [withSymbol(value, currency), costTooltipLabel]}
              />
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f3f3" />
              <XAxis dataKey="name" fontSize={12} stroke="#52525b" axisLine={false} tickLine={false} />
              <YAxis
                fontSize={12}
                stroke="#52525b"
                tickFormatter={(v: number) => withSymbol(v, currency)}
                axisLine={false}
                tickLine={false}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={16}>
                {satelliteData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 안테나 비율 */}
      <div className="rounded-lg border border-[#f3f3f3] bg-white p-4">
        <Text size="small" weight="medium" color="primary" className="mb-4">
          {antennaChartTitle}
        </Text>
        <div className="flex h-64 items-center justify-center">
          <ResponsiveContainer width={240} height={240}>
            <PieChart>
              <Tooltip
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid #f3f3f3',
                  fontFamily: 'Pretendard',
                  fontSize: 12,
                  color: '#52525b'
                }}
                formatter={(value: number, name: string) => [`${value}%`, name]}
              />
              <Pie
                data={antennaData}
                cx="50%"
                cy="50%"
                innerRadius={74}
                outerRadius={90}
                cornerRadius={4}
                dataKey="value"
                startAngle={90}
                endAngle={450}
                labelLine={false}
                label={renderCustomizedLabel}
              >
                {antennaData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 결제 수단 */}
      <div className="rounded-xl border border-[#f3f3f3] bg-white p-4">
        <Text size="small" weight="medium" color="primary" className="mb-4">
          {paymentMethodChartTitle}
        </Text>
        <div className="relative flex flex-col gap-4 h-52">
          <div className="flex h-4 w-full gap-[2px]">
            {paymentMethodData.map((item) => (
              <div key={item.name} style={{ flexBasis: `${item.percentage}%` }} className="group relative h-full rounded">
                <div className="h-full w-full rounded" style={{ backgroundColor: item.color }} />
                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[4px] border border-[#f3f3f3] bg-white px-2 py-1 text-[12px] font-medium text-[#52525b] opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                  {`${item.name} ${item.percentage}%`}
                </span>
              </div>
            ))}
          </div>
          {paymentMethodData.map((item) => (
            <div key={item.name} className="flex justify-between text-sm px-1">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                <Text size="small" weight="medium" color="secondary">
                  {item.name}
                </Text>
              </div>
              <Text size="small" weight="medium" color="secondary">
                {item.percentage}%
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
